import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { format } from "date-fns"
import { Search, SlidersHorizontal, X } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import { OffersAdminGrid } from "@/features/offers-admin/OffersAdminGrid"
import { OffersKpis } from "@/features/offers-admin/OffersKpis"
import type { OffersAdminInput } from "@/features/offers-admin/offers-admin-datasource"
import { QuickFilterToggle } from "@/features/offers-admin/QuickFilterToggle"
import { useDebounce } from "@/hooks/use-debounce"
import { useNegotiationMetrics } from "@/queries/marketplace"
import type { AdminNegotiationFiltersInput } from "@/types/marketplace.types"

export const Route = createFileRoute("/offers/")({
	component: OffersPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePositiveInt(search.page),
		// Column-managed filters (CSV-encoded, owned by the grid).
		status: search.status as string | undefined,
		awaiting: search.awaiting as string | undefined,
		currency: search.currency as string | undefined,
		voidReason: search.voidReason as string | undefined,
		// Non-column filters
		redeemed: search.redeemed as string | undefined,
		minDaysWaiting: normalizePositiveInt(search.minDaysWaiting),
		search: search.search as string | undefined,
		dateFrom: normalizeDateString(search.dateFrom),
		dateTo: normalizeDateString(search.dateTo),
		sortBy: search.sortBy as string | undefined,
		sortOrder: search.sortOrder as string | undefined,
	}),
})

function normalizePositiveInt(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const i = Math.floor(n)
	return i > 0 ? i : undefined
}

function normalizeDateString(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const d = new Date(raw)
	return Number.isNaN(d.getTime()) ? undefined : raw
}

function DebouncedSearchInput({
	defaultValue,
	onDebouncedChange,
	inputRef,
}: {
	defaultValue: string
	onDebouncedChange: (value: string) => void
	inputRef?: React.RefObject<{ clear: () => void } | null>
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		onDebouncedChange(debouncedValue)
	}, [debouncedValue, onDebouncedChange])

	React.useImperativeHandle(inputRef, () => ({
		clear: () => setValue(""),
	}))

	return (
		<div className="relative w-[240px] shrink-0">
			<Search className="absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-meta" />
			<Input
				placeholder="Offer id, @buyer, @seller"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="pl-8"
			/>
		</div>
	)
}

function DebouncedDaysInput({
	defaultValue,
	onDebouncedChange,
}: {
	defaultValue: string
	onDebouncedChange: (days: number | undefined) => void
}) {
	const [value, setValue] = React.useState(defaultValue)
	const debouncedValue = useDebounce(value, 150)
	const isInitialMount = React.useRef(true)

	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
			return
		}
		const days = Number.parseInt(debouncedValue, 10)
		onDebouncedChange(days > 0 ? days : undefined)
	}, [debouncedValue, onDebouncedChange])

	return (
		<div className="flex items-center gap-2">
			<Input
				id="offers-idle-days"
				type="number"
				min={1}
				inputMode="numeric"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="w-20"
			/>
			<span className="text-muted-foreground">days or more</span>
		</div>
	)
}

function OffersPage() {
	const searchParams = useSearch({ from: "/offers/" })
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1
	const statusFilter = searchParams.status ?? ""
	const awaitingFilter = searchParams.awaiting ?? ""

	const dateRange: DateRange | undefined = React.useMemo(() => {
		if (!searchParams.dateFrom && !searchParams.dateTo) return undefined
		return {
			from: searchParams.dateFrom ? new Date(searchParams.dateFrom) : undefined,
			to: searchParams.dateTo ? new Date(searchParams.dateTo) : undefined,
		}
	}, [searchParams.dateFrom, searchParams.dateTo])

	const searchRef = React.useRef<{ clear: () => void }>(null)
	const metrics = useNegotiationMetrics()

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/offers",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === "all" || val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: value || undefined, page: undefined })
		},
		[updateFilters],
	)

	const handleDebouncedDays = React.useCallback(
		(days: number | undefined) => {
			updateFilters({ minDaysWaiting: days, page: undefined })
		},
		[updateFilters],
	)

	const sortBy = (searchParams.sortBy ??
		"updatedAt") as AdminNegotiationFiltersInput["sortBy"]
	const sortOrder = (searchParams.sortOrder ??
		"desc") as AdminNegotiationFiltersInput["sortOrder"]

	const gridInput = React.useMemo<OffersAdminInput>(() => {
		const filters: OffersAdminInput = { sortBy, sortOrder }
		if (searchParams.search) filters.search = searchParams.search
		if (searchParams.redeemed === "true") filters.redeemed = true
		if (searchParams.redeemed === "false") filters.redeemed = false
		if (searchParams.dateFrom) filters.dateFrom = searchParams.dateFrom
		if (searchParams.dateTo) filters.dateTo = searchParams.dateTo
		if (searchParams.minDaysWaiting)
			filters.minDaysWaiting = searchParams.minDaysWaiting
		return filters
	}, [
		searchParams.search,
		searchParams.redeemed,
		searchParams.dateFrom,
		searchParams.dateTo,
		searchParams.minDaysWaiting,
		sortBy,
		sortOrder,
	])

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(
			nextSortBy: AdminNegotiationFiltersInput["sortBy"],
			nextSortOrder: AdminNegotiationFiltersInput["sortOrder"],
		) => {
			const isDefault = nextSortBy === "updatedAt" && nextSortOrder === "desc"
			updateFilters({
				sortBy: isDefault ? undefined : nextSortBy,
				sortOrder: isDefault ? undefined : nextSortOrder,
				page: undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/offers", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters =
		!!searchParams.status ||
		!!searchParams.awaiting ||
		!!searchParams.currency ||
		!!searchParams.voidReason ||
		!!searchParams.redeemed ||
		dateRange !== undefined ||
		!!searchParams.minDaysWaiting ||
		!!searchParams.search

	// Chip presets set several params at once; a chip reads as active only
	// when every param it owns matches, and toggling it off clears them all.
	const isPreset = React.useCallback(
		(preset: Record<string, string | number | undefined>) =>
			Object.entries(preset).every(
				([key, value]) =>
					String(searchParams[key as keyof typeof searchParams] ?? "") ===
					String(value ?? ""),
			),
		[searchParams],
	)

	const togglePreset = React.useCallback(
		(preset: Record<string, string | number | undefined>) => {
			const active = isPreset(preset)
			const cleared = Object.fromEntries(
				Object.keys(preset).map((key) => [key, undefined]),
			)
			updateFilters({ ...(active ? cleared : preset), page: undefined })
		},
		[isPreset, updateFilters],
	)

	const counts = metrics.data
	const extraFilterCount =
		(dateRange !== undefined ? 1 : 0) + (searchParams.minDaysWaiting ? 1 : 0)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Offers"
				description="Price negotiations between buyers and sellers"
			/>

			<OffersKpis />

			<div className="min-h-0 flex-1">
				<OffersAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					toolbar={
						<>
							<DebouncedSearchInput
								defaultValue={searchParams.search ?? ""}
								onDebouncedChange={handleDebouncedSearch}
								inputRef={searchRef}
							/>

							<QuickFilterToggle
								active={
									statusFilter === "pending,countered" &&
									awaitingFilter === "seller"
								}
								count={counts?.open.awaitingSeller}
								onToggle={() =>
									togglePreset({
										status: "pending,countered",
										awaiting: "seller",
									})
								}
							>
								On seller
							</QuickFilterToggle>
							<QuickFilterToggle
								active={
									statusFilter === "pending,countered" &&
									awaitingFilter === "buyer"
								}
								count={counts?.open.awaitingBuyer}
								onToggle={() =>
									togglePreset({
										status: "pending,countered",
										awaiting: "buyer",
									})
								}
							>
								On buyer
							</QuickFilterToggle>
							<QuickFilterToggle
								active={isPreset({ status: "accepted", redeemed: "false" })}
								count={counts?.acceptedUnredeemed.total}
								onToggle={() =>
									togglePreset({ status: "accepted", redeemed: "false" })
								}
							>
								Awaiting checkout
							</QuickFilterToggle>
							<QuickFilterToggle
								active={isPreset({ redeemed: "true" })}
								count={counts?.allTime.redeemed}
								onToggle={() => togglePreset({ redeemed: "true" })}
							>
								Redeemed
							</QuickFilterToggle>
							<QuickFilterToggle
								active={isPreset({ status: "voided" })}
								count={counts?.allTime.voided}
								onToggle={() => togglePreset({ status: "voided" })}
							>
								Voided
							</QuickFilterToggle>
							<QuickFilterToggle
								active={isPreset({
									status: "pending,countered",
									minDaysWaiting: 2,
								})}
								count={counts?.open.staleOver48h}
								onToggle={() =>
									togglePreset({
										status: "pending,countered",
										minDaysWaiting: 2,
									})
								}
							>
								Idle over 2 days
							</QuickFilterToggle>

							<Popover>
								<PopoverTrigger asChild>
									<Button variant="outline">
										<SlidersHorizontal />
										Filters
										{extraFilterCount > 0 && (
											<span className="text-meta tabular-nums">
												{extraFilterCount}
											</span>
										)}
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-auto p-4" align="start">
									<div className="flex flex-col gap-4">
										<div className="flex flex-col gap-2">
											<span className="text-xs text-meta">
												Opened between
												{dateRange?.from && (
													<span className="ml-1.5 text-foreground">
														{format(dateRange.from, "LLL dd, y")}
														{dateRange.to &&
															` to ${format(dateRange.to, "LLL dd, y")}`}
													</span>
												)}
											</span>
											<Calendar
												initialFocus
												mode="range"
												defaultMonth={dateRange?.from}
												selected={dateRange}
												onSelect={(range) =>
													updateFilters({
														dateFrom: range?.from?.toISOString() || undefined,
														dateTo: range?.to?.toISOString() || undefined,
														page: undefined,
													})
												}
												numberOfMonths={2}
											/>
										</div>
										<div className="flex flex-col gap-2">
											<label
												htmlFor="offers-idle-days"
												className="text-xs text-meta"
											>
												Without a reply for
											</label>
											<DebouncedDaysInput
												defaultValue={
													searchParams.minDaysWaiting
														? String(searchParams.minDaysWaiting)
														: ""
												}
												onDebouncedChange={handleDebouncedDays}
											/>
										</div>
									</div>
								</PopoverContent>
							</Popover>

							{hasActiveFilters && (
								<Button variant="ghost" onClick={clearFilters}>
									<X />
									Clear
								</Button>
							)}
						</>
					}
				/>
			</div>
		</div>
	)
}
